import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Phone, Download, Wand2, Mail, History, X, RotateCcw } from "lucide-react";

const WhatsAppIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
import Nav from "@/components/Nav";
import Wordmark from "@/components/Wordmark";
import CinematicLoader from "@/components/CinematicLoader";
import QuoteModal from "@/components/QuoteModal";
import ReviseModal from "@/components/ReviseModal";
import HistoryModal from "@/components/HistoryModal";
import { useAuth } from "@/context/AuthContext";
import { BLUEPRINT } from "@/constants/testIds";

const FIELD_LABELS = {
  title: "Title",
  tagline: "Tagline",
  executive_summary: "Executive summary",
  creative_direction: "Creative direction",
  moodboard: "Moodboard",
  production_type: "Format & scope",
  deliverables: "Deliverables",
  budget: "Budget",
  timeline: "Timeline",
  casting: "Casting",
  locations: "Locations",
  equipment: "Equipment",
  crew: "Crew",
  next_steps: "Next steps",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bullets = ({ items }) => (
  <ul className="space-y-3">
    {(items || []).map((it, i) => (
      <li key={`${String(it).slice(0, 40)}-${i}`} className="flex gap-3 items-start">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-denim-bnm shrink-0" />
        <span className="text-ink text-[15.5px] leading-[1.65]">{it}</span>
      </li>
    ))}
  </ul>
);

function emailLabel(sending, sent) {
  if (sent) return "Sent to your inbox";
  if (sending) return "Sending…";
  return "Email me the plan";
}

const ActHeader = ({ n, kicker, title, testId }) => (
  <div className="mt-16 md:mt-24 mb-10 md:mb-14" data-testid={testId}>
    <div className="flex items-center gap-4 mb-6">
      <div className="font-display font-extrabold text-[52px] md:text-[80px] leading-none tracking-[-0.05em] text-denim-bnm">
        {n}
      </div>
      <div className="eyebrow eyebrow-muted">{kicker}</div>
    </div>
    <h2 className="font-display font-extrabold text-[34px] sm:text-[48px] md:text-[64px] tracking-[-0.04em] leading-[1.0] text-ink max-w-[900px]">
      {title}
    </h2>
  </div>
);

const Row = ({ eyebrow, title, children, testId }) => (
  <section className="py-8 md:py-12 border-t border-bnm" data-testid={testId}>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
      <div className="md:col-span-4">
        <div className="eyebrow eyebrow-denim mb-2.5">{eyebrow}</div>
        <h3 className="font-display font-semibold text-[22px] md:text-[26px] tracking-[-0.02em] leading-[1.2] text-ink">
          {title}
        </h3>
      </div>
      <div className="md:col-span-8">{children}</div>
    </div>
  </section>
);

export default function Blueprint() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const isSignedIn = !!user;
  const [bp, setBp] = useState(state?.blueprint || null);
  const [loading, setLoading] = useState(!state?.blueprint);
  const [showQuote, setShowQuote] = useState(false);
  const [showRevise, setShowRevise] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [diffDismissed, setDiffDismissed] = useState(false);
  const [undoVisible, setUndoVisible] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const undoTimerRef = useRef(null);

  useEffect(() => {
    if (bp) return;
    let on = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blueprint/${id}`);
        if (on) setBp(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [id, bp]);

  // 30-second undo pill, fires whenever last_revision changes and belongs to the current user
  const lastRevisionId = bp?.last_revision?.id;
  const isOwnerFlag = !!(user && bp?.user_id === user.user_id);
  useEffect(() => {
    if (!isOwnerFlag || !lastRevisionId) {
      setUndoVisible(false);
      return undefined;
    }
    setUndoVisible(true);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setUndoVisible(false), 30_000);
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, [lastRevisionId, isOwnerFlag]);

  if (loading || !bp) return <CinematicLoader />;

  const handleDownload = () => {
    window.print();
  };

  const handleEmail = async () => {
    if (emailSending || emailSent || !user?.email) return;
    setEmailSending(true);
    try {
      await axios.post(`${API}/blueprint/${bp.id}/email`, {});
      setEmailSent(true);
    } catch (e) {
      console.error(e);
      setEmailSending(false);
    }
  };

  const isOwner = isOwnerFlag;
  const showSignInNudge = !isSignedIn;
  const showDiff = isOwner && bp.last_revision && !diffDismissed;

  const shareOnWhatsApp = () => {
    const url = typeof window !== "undefined"
      ? `${window.location.origin}/blueprint/${bp.id}`
      : `/blueprint/${bp.id}`;
    const message = `Your Producer at Brand New Money Films just put together a plan for me, "${bp.title}".\n\nHave a look: ${url}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  const handleUndo = async () => {
    if (undoing || !bp.last_revision?.id) return;
    setUndoing(true);
    try {
      const { data } = await axios.post(
        `${API}/blueprint/${bp.id}/revisions/${bp.last_revision.id}/restore`,
        {}
      );
      setBp(data);
      setUndoVisible(false);
      setDiffDismissed(false);
    } catch (e) {
      console.error(e);
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink" data-testid={BLUEPRINT.page}>
      <Nav />

      {/* Sign-in nudge (anonymous only) */}
      {showSignInNudge && (
        <div
          data-testid="signin-nudge"
          data-no-print="true"
          className="border-b border-bnm bg-[#fafafa] no-print"
        >
          <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-3.5 md:py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
            <span className="w-1.5 h-1.5 rounded-full bg-denim-bnm shrink-0 hidden sm:inline-block" />
            <div className="text-[13.5px] md:text-[14px] text-ink flex-1">
              <span className="text-muted-ink">A note from your Producer, </span>
              Sign in to save <span className="italic">&ldquo;{bp.title}&rdquo;</span> to your projects and see the full plan.
            </div>
            <button
              type="button"
              onClick={signIn}
              className="text-[13px] px-4 py-2 rounded-full bg-ink text-white hover:bg-black transition-colors inline-flex items-center gap-2 shrink-0"
              data-testid="signin-nudge-cta"
            >
              Sign in
            </button>
          </div>
        </div>
      )}

      {/* Cover */}
      <header className="max-w-[1180px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-14">
        <button
          type="button"
          onClick={() => navigate(user ? "/projects" : "/")}
          className="inline-flex items-center gap-2 text-muted-ink text-sm hover:text-ink transition-colors mb-10 no-print"
          data-no-print="true"
          data-testid={BLUEPRINT.ctaBack}
        >
          <ArrowLeft className="w-4 h-4" /> {user ? "Your projects" : "Back to start"}
        </button>

        {/* Action rail (signed in only) */}
        {isSignedIn && (
          <div
            className="flex flex-wrap items-center gap-2 mb-10 no-print"
            data-no-print="true"
            data-testid="blueprint-actions"
          >
            {isOwner && (
              <button
                type="button"
                onClick={() => setShowRevise(true)}
                className="pill inline-flex items-center gap-2"
                data-testid="action-revise"
              >
                <Wand2 className="w-3.5 h-3.5 text-denim-bnm" />
                <span>Revise with Your Producer</span>
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                className="pill inline-flex items-center gap-2"
                data-testid="action-history"
              >
                <History className="w-3.5 h-3.5" />
                <span>History</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              className="pill inline-flex items-center gap-2"
              data-testid="action-download"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download PDF</span>
            </button>
            <button
              type="button"
              onClick={handleEmail}
              disabled={emailSending || emailSent}
              className="pill inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="action-email"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{emailLabel(emailSending, emailSent)}</span>
            </button>
            <button
              type="button"
              onClick={shareOnWhatsApp}
              className="pill inline-flex items-center gap-2"
              data-testid="action-whatsapp"
              title="Share on WhatsApp"
            >
              <WhatsAppIcon className="text-denim-bnm" />
              <span>Share on WhatsApp</span>
            </button>
          </div>
        )}

        {/* Diff banner, subtle "you just changed X, Y, Z" */}
        {showDiff && (
          <div
            className="mb-10 bnm-card p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 no-print"
            data-no-print="true"
            data-testid="diff-banner"
          >
            <div className="flex-1 min-w-0">
              <div className="eyebrow eyebrow-denim mb-2">Just reworked</div>
              <div className="text-[13.5px] md:text-[14px] text-ink leading-[1.5]">
                <span className="text-muted-ink">You changed </span>
                {(bp.last_revision.changed_fields || []).map((f, i, arr) => (
                  <React.Fragment key={f}>
                    <span className="italic">{FIELD_LABELS[f] || f}</span>
                    {i < arr.length - 1 && <span className="text-muted-ink">, </span>}
                  </React.Fragment>
                ))}
                <span className="text-muted-ink">.</span>
              </div>
              {bp.last_revision.instruction && (
                <div className="text-[13px] text-muted-ink italic mt-1.5 truncate">
                  &ldquo;{bp.last_revision.instruction}&rdquo;
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                className="pill text-[12.5px] py-2 px-3.5"
                data-testid="diff-view-history"
              >
                View history
              </button>
              <button
                type="button"
                onClick={() => setDiffDismissed(true)}
                aria-label="dismiss"
                className="w-8 h-8 rounded-full border border-bnm flex items-center justify-center hover:border-black"
                data-testid="diff-dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        <div className="eyebrow eyebrow-denim mb-5">A note from your Producer</div>
        <h1
          data-testid={BLUEPRINT.title}
          className="font-display font-extrabold text-[44px] sm:text-[64px] md:text-[88px] leading-[0.94] tracking-[-0.045em] text-ink"
        >
          {bp.title}
        </h1>
        {bp.tagline && (
          <p
            data-testid={BLUEPRINT.tagline}
            className="mt-6 md:mt-8 font-serif-italic font-light text-[22px] md:text-[30px] text-ink/85 max-w-[820px] leading-[1.3]"
          >
            &ldquo;{bp.tagline}&rdquo;
          </p>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13.5px]">
          <span className="eyebrow eyebrow-muted">The brief</span>
          <span className="text-muted-ink italic">&ldquo;{bp.idea}&rdquo;</span>
          {bp.audience && (
            <>
              <span className="text-muted-ink">·</span>
              <span className="text-muted-ink"><span className="eyebrow eyebrow-muted mr-2">For</span>{bp.audience}</span>
            </>
          )}
        </div>
      </header>

      <main className="max-w-[1180px] mx-auto px-6 md:px-10 pb-16">
        {/* =========================== ACT I, THE STORY =========================== */}
        <ActHeader n="I" kicker="Act one" title="The story." testId="act-1" />

        <Row eyebrow="01" title="What we're making." testId={`${BLUEPRINT.section}-summary`}>
          <p className="text-ink text-[16.5px] md:text-[17.5px] leading-[1.8] max-w-[780px]">{bp.executive_summary}</p>
        </Row>

        <Row eyebrow="02" title="How it should feel." testId={`${BLUEPRINT.section}-creative`}>
          <p className="text-ink text-[16.5px] md:text-[17.5px] leading-[1.8] max-w-[780px]">{bp.creative_direction}</p>
        </Row>

        <Row eyebrow="03" title="Moodboard." testId={`${BLUEPRINT.section}-moodboard`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {(bp.reference_images || []).map((src, i) => (
              <div key={src || `ref-${i}`} className="ref-img aspect-[4/5]">
                <img
                  src={src}
                  alt={bp.moodboard?.[i] || `reference ${i + 1}`}
                  loading="lazy"
                  data-testid={`${BLUEPRINT.moodboardImage}-${i}`}
                />
              </div>
            ))}
          </div>
          <ul className="mt-5 flex flex-wrap gap-2">
            {(bp.moodboard || []).map((m, i) => (
              <li key={`${m}-${i}`} className="text-[12.5px] eyebrow eyebrow-muted border border-bnm rounded-full px-3 py-1.5">
                {m}
              </li>
            ))}
          </ul>
        </Row>

        {/* =========================== ACT II, THE PRODUCTION =========================== */}
        <ActHeader n="II" kicker="Act two" title="The production." testId="act-2" />

        {!isSignedIn ? (
          <section className="py-14 md:py-20 border-t border-bnm" data-testid="blueprint-lock">
            <div className="relative">
              <div className="filter blur-[7px] select-none pointer-events-none space-y-10">
                {["Format & scope", "Deliverables", "Casting", "Locations", "Equipment", "Crew", "Timeline"].map((t, i) => (
                  <div key={t} className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-4">
                      <div className="eyebrow eyebrow-denim mb-2.5">{String(i + 4).padStart(2, "0")}</div>
                      <div className="font-display font-extrabold text-[22px] md:text-[26px] tracking-[-0.02em] leading-[1.15] text-ink">{t}</div>
                    </div>
                    <div className="md:col-span-8 space-y-3">
                      <div className="h-3 bg-[#ececec] rounded-full w-11/12" />
                      <div className="h-3 bg-[#ececec] rounded-full w-9/12" />
                      <div className="h-3 bg-[#ececec] rounded-full w-10/12" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white border border-bnm rounded-2xl px-7 md:px-10 py-10 md:py-12 max-w-[540px] mx-auto text-center shadow-bnm">
                  <div className="eyebrow eyebrow-denim mb-3">A note from your Producer</div>
                  <h3 className="font-display font-extrabold text-[24px] md:text-[30px] tracking-[-0.025em] leading-[1.15] text-ink">
                    Sign in to see the
                    <br />
                    <span className="font-serif-italic font-light text-denim-bnm">full plan</span>.
                  </h3>
                  <p className="text-muted-ink text-[14.5px] mt-4 leading-[1.65] max-w-[420px] mx-auto">
                    Deliverables, casting, locations, crew, equipment, timeline and next steps, yours when you sign in. So you can come back to this project anytime.
                  </p>
                  <button
                    type="button"
                    onClick={signIn}
                    className="btn-denim mt-7"
                    data-testid="blueprint-unlock"
                  >
                    <span>Sign in</span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <>
            <Row eyebrow="04" title="Format & scope." testId={`${BLUEPRINT.section}-type`}>
              <p className="text-ink text-[16.5px] leading-[1.7]">{bp.production_type}</p>
            </Row>

            <Row eyebrow="05" title="What you'll receive." testId={`${BLUEPRINT.section}-deliverables`}>
              <Bullets items={bp.deliverables} />
            </Row>

            <Row eyebrow="06" title="Who's on camera." testId={`${BLUEPRINT.section}-casting`}>
              <Bullets items={bp.casting} />
            </Row>

            <Row eyebrow="07" title="Where we shoot." testId={`${BLUEPRINT.section}-locations`}>
              <Bullets items={bp.locations} />
            </Row>

            <Row eyebrow="08" title="The gear list." testId={`${BLUEPRINT.section}-equipment`}>
              <Bullets items={bp.equipment} />
            </Row>

            <Row eyebrow="09" title="The team behind the camera." testId={`${BLUEPRINT.section}-crew`}>
              <Bullets items={bp.crew} />
            </Row>

            <Row eyebrow="10" title="From green-light to delivery." testId={`${BLUEPRINT.section}-timeline`}>
              <div className="bnm-card p-6 md:p-8 max-w-[760px]">
                <p className="text-ink text-[16.5px] leading-[1.7]">{bp.timeline}</p>
              </div>
            </Row>

            {/* =========================== ACT III, THE INVESTMENT =========================== */}
            <ActHeader n="III" kicker="Act three" title="The investment." testId="act-3" />

            <Row eyebrow="11" title="Honest numbers." testId={`${BLUEPRINT.section}-budget`}>
              <div className="bnm-card p-6 md:p-8 max-w-[640px]">
                <div className="eyebrow eyebrow-muted mb-2">Estimated range</div>
                <div className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.025em] text-ink">
                  {bp.budget}
                </div>
              </div>
            </Row>

            <Row eyebrow="12" title="What we need from you." testId={`${BLUEPRINT.section}-next`}>
              <Bullets items={bp.next_steps} />
            </Row>
          </>
        )}
      </main>

      {/* End CTA */}
      <section className="border-t border-bnm bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-36 text-center">
          <div className="eyebrow eyebrow-denim mb-6">Ready when you are</div>
          <h2 className="font-display font-extrabold text-[34px] sm:text-[48px] md:text-[64px] tracking-[-0.04em] leading-[1.0] text-ink">
            Your blueprint is{" "}
            <span className="font-serif-italic font-light text-denim-bnm">ready</span>.
          </h2>
          <p className="text-muted-ink mt-7 text-[16.5px] md:text-[18px] leading-[1.65] max-w-[640px] mx-auto">
            Your Producer has planned every <span className="font-serif-italic text-ink">stage</span>. The next step is bringing your <span className="font-serif-italic text-ink">vision</span> to life.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowQuote(true)}
              className="btn-denim w-full sm:w-auto"
              data-testid={BLUEPRINT.ctaRequestQuote}
            >
              <span>Request a Quote</span>
            </button>
            <a
              href="tel:+919258651242"
              className="btn-ghost w-full sm:w-auto"
              data-testid={BLUEPRINT.ctaBookCall}
            >
              <Phone className="w-4 h-4" />
              <span>Call Your Producer</span>
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-bnm">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <Wordmark size="sm" />
          <div className="text-[13px] text-muted-ink">
            Prepared by Your Producer · {new Date(bp.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      </footer>

      {showQuote && <QuoteModal blueprintId={bp.id} onClose={() => setShowQuote(false)} />}
      {showRevise && (
        <ReviseModal
          blueprintId={bp.id}
          initialBlueprint={bp}
          onClose={() => setShowRevise(false)}
          onRevised={(updated) => {
            setBp(updated);
            setDiffDismissed(false);
            // Keep the modal open so users can keep talking; the diff will show
            // when they close the modal.
          }}
        />
      )}
      {showHistory && (
        <HistoryModal
          blueprintId={bp.id}
          onClose={() => setShowHistory(false)}
          onRestored={(updated) => {
            setBp(updated);
            setShowHistory(false);
            setDiffDismissed(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}

      {/* Undo pill, floating, auto-hides after 30s */}
      {isOwner && undoVisible && bp.last_revision?.id && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-6 md:bottom-8 z-[70] no-print"
          data-no-print="true"
          data-testid="undo-pill"
        >
          <div className="bg-ink text-white rounded-full pl-5 pr-1.5 py-1.5 flex items-center gap-3 shadow-[0_18px_40px_rgba(10,10,10,0.28)]">
            <span className="text-[13px]">
              Reworked <span className="text-white/60">·</span>{" "}
              <span className="text-white/70">{bp.last_revision.changed_fields?.length || 0} changes</span>
            </span>
            <button
              type="button"
              onClick={handleUndo}
              disabled={undoing}
              className="inline-flex items-center gap-1.5 bg-white text-ink text-[13px] font-medium px-4 py-2 rounded-full hover:bg-[#f0f0f0] transition-colors disabled:opacity-60"
              data-testid="undo-pill-cta"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {undoing ? "Undoing…" : "Undo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
